import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Skeleton,
  Stack,
  TagsInput,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCirclePlus, IconEdit, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

const TYPES = [
  'text',
  'number',
  'textarea',
  'select',
  'email',
  'url',
  'date',
  'time',
  'datetime-local',
] as const;
type FieldType = (typeof TYPES)[number];

/** Custom contact fields CRUD — the "Custom fields" tab of the Contacts hub. */
export function CustomFieldsPanel() {
  const list = trpc.contacts.customFields.list.useQuery();
  const utils = trpc.useUtils();
  const [opened, { open, close }] = useDisclosure(false);
  const [name, setName] = useState('');
  const [type, setType] = useState<FieldType>('text');
  const [options, setOptions] = useState<string[]>([]);
  // null = create mode; { fieldId } = edit mode.
  const [editing, setEditing] = useState<{ fieldId: number } | null>(null);

  const resetForm = () => {
    setName('');
    setType('text');
    setOptions([]);
    setEditing(null);
  };

  const create = trpc.contacts.customFields.create.useMutation({
    onSuccess: () => {
      void utils.contacts.customFields.list.invalidate();
      close();
      notifications.show({ message: 'Field created', color: 'green' });
      resetForm();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const update = trpc.contacts.customFields.update.useMutation({
    onSuccess: () => {
      void utils.contacts.customFields.list.invalidate();
      close();
      notifications.show({ message: 'Field updated', color: 'green' });
      resetForm();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const del = trpc.contacts.customFields.delete.useMutation({
    onSuccess: () => void utils.contacts.customFields.list.invalidate(),
  });

  const startEdit = (f: {
    id: number;
    inputName: string;
    inputType: string;
    options: string | null;
  }) => {
    setEditing({ fieldId: f.id });
    setName(f.inputName);
    setType(
      (TYPES as readonly string[]).includes(f.inputType) ? (f.inputType as FieldType) : 'text',
    );
    try {
      setOptions(f.options ? (JSON.parse(f.options) as string[]) : []);
    } catch {
      setOptions([]);
    }
    open();
  };
  const handleClose = () => {
    resetForm();
    close();
  };

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button leftSection={<IconCirclePlus size={14} />} onClick={open}>
          New field
        </Button>
      </Group>
      <Card p={0}>
        {list.isPending ? (
          <Skeleton height={200} m="md" />
        ) : (
          <Table striped highlightOnHover withRowBorders={false}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Options</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(list.data ?? []).map((f) => {
                const opts: string[] = (() => {
                  try {
                    return f.options ? (JSON.parse(f.options) as string[]) : [];
                  } catch {
                    return [];
                  }
                })();
                return (
                  <Table.Tr key={f.id}>
                    <Table.Td>
                      <Text ff="monospace" size="sm">
                        {f.inputName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="xs">
                        {f.inputType}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {opts.map((o) => (
                          <Badge key={o} size="xs" variant="default">
                            {o}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td align="right">
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <ActionIcon variant="subtle" onClick={() => startEdit(f)} aria-label="Edit">
                          <IconEdit size={14} />
                        </ActionIcon>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => del.mutate({ fieldId: f.id })}
                          aria-label="Delete"
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Modal
        opened={opened}
        onClose={handleClose}
        title={editing ? 'Edit custom field' : 'New custom field'}
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="Field name"
            description="Letters / digits / underscores only"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <Select
            label="Type"
            data={TYPES.map((t) => ({ value: t, label: t }))}
            value={type}
            onChange={(v) => setType((v as FieldType) ?? 'text')}
          />
          {type === 'select' && <TagsInput label="Options" value={options} onChange={setOptions} />}
          <Button
            loading={create.isPending || update.isPending}
            onClick={() => {
              const payload = {
                inputName: name,
                inputType: type,
                ...(type === 'select' ? { options } : {}),
              };
              if (editing) update.mutate({ ...payload, fieldId: editing.fieldId });
              else create.mutate(payload);
            }}
          >
            {editing ? 'Save changes' : 'Create'}
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
